import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { 
  Search, ChevronRight, Calendar, Eye, Clock, 
  ArrowLeft, Share2, BookOpen, Heart, ShoppingBag, ExternalLink, Star
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useAds } from '../context/AdsContext';
import { useProducts } from '../context/ProductsContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Category icons mapping
const CATEGORY_ICONS = {
  vaccination: '💉',
  prevention: '🛡️',
  nutrition: '🥗',
  comportement: '🧠',
  urgences: '🚨',
  soins: '🩺',
  adoption: '🏠',
  autre: '📝'
};

// Ad Banner Component
const AdBanner = ({ ad, onImpression, onClick }) => {
  useEffect(() => {
    if (ad && onImpression) {
      onImpression(ad.id);
    }
  }, [ad?.id]);

  if (!ad) return null;

  return (
    <a 
      href={ad.linkUrl}
      target="_blank"
      rel="noopener noreferrer sponsored"
      onClick={() => onClick && onClick(ad.id)}
      className="block overflow-hidden rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-all group"
      data-testid={`ad-banner-${ad.id}`}
    >
      <div className="relative">
        <img 
          src={ad.imageUrl}
          alt={ad.altText}
          className="w-full h-auto object-cover group-hover:scale-105 transition-transform duration-300"
        />
        <span className="absolute top-2 right-2 px-2 py-0.5 bg-black/50 text-white text-xs rounded">
          Publicité
        </span>
      </div>
      <div className="p-3 bg-gray-50">
        <p className="text-xs text-gray-500">{ad.advertiser}</p>
      </div>
    </a>
  );
};

// Product Card Component for Sidebar
const ProductCard = ({ product }) => {
  const discount = product.originalPrice 
    ? Math.round((1 - product.price / product.originalPrice) * 100)
    : 0;

  return (
    <a 
      href={product.affiliateUrl}
      target="_blank"
      rel="noopener noreferrer sponsored"
      className="block bg-white rounded-xl border border-gray-100 overflow-hidden hover:shadow-md transition-all group"
      data-testid={`product-card-${product.id}`}
    >
      <div className="relative">
        <img 
          src={product.image}
          alt={product.name}
          className="w-full h-32 object-cover group-hover:scale-105 transition-transform duration-300"
        />
        {discount > 0 && (
          <span className="absolute top-2 right-2 px-2 py-0.5 bg-red-500 text-white text-xs font-bold rounded">
            -{discount}%
          </span>
        )}
      </div>
      <div className="p-3">
        <p className="text-xs text-gray-500 mb-1">{product.brand}</p>
        <h4 className="font-medium text-sm text-gray-800 line-clamp-2 group-hover:text-green-600 transition-colors mb-2">
          {product.name}
        </h4>
        <div className="flex items-center gap-1 mb-2">
          <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
          <span className="text-xs text-gray-600">{product.rating}</span>
          <span className="text-xs text-gray-400">({product.reviews})</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="font-bold text-green-600">{product.price}€</span>
          {product.originalPrice && (
            <span className="text-xs text-gray-400 line-through">{product.originalPrice}€</span>
          )}
        </div>
      </div>
    </a>
  );
};

// Sidebar Component with Ads and Products
const Sidebar = ({ relatedSpecies }) => {
  const { getAdsByPosition, recordClick, recordImpression } = useAds();
  const { getProductsByAnimal, getFeaturedProducts } = useProducts();

  const sidebarAds = getAdsByPosition('sidebar');
  
  // Get products related to the article's species
  let relevantProducts = [];
  if (relatedSpecies && relatedSpecies.length > 0) {
    relatedSpecies.forEach(species => {
      relevantProducts = [...relevantProducts, ...getProductsByAnimal(species)];
    });
    // Remove duplicates
    relevantProducts = [...new Map(relevantProducts.map(p => [p.id, p])).values()];
  }
  
  // If no species-specific products, show featured
  if (relevantProducts.length === 0) {
    relevantProducts = getFeaturedProducts();
  }
  
  // Limit to 3 products
  relevantProducts = relevantProducts.slice(0, 3);

  return (
    <aside className="space-y-6">
      {/* Sidebar Ad */}
      {sidebarAds.length > 0 && (
        <div>
          <AdBanner 
            ad={sidebarAds[0]} 
            onImpression={recordImpression}
            onClick={recordClick}
          />
        </div>
      )}

      {/* Related Products */}
      {relevantProducts.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-100 p-4">
          <div className="flex items-center gap-2 mb-4">
            <ShoppingBag className="w-5 h-5 text-green-600" />
            <h3 className="font-semibold text-gray-800">Produits recommandés</h3>
          </div>
          <div className="space-y-4">
            {relevantProducts.map(product => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
          <Button 
            variant="outline" 
            className="w-full mt-4 text-green-600 border-green-200 hover:bg-green-50"
            onClick={() => window.location.href = '/boutique'}
          >
            Voir la boutique
            <ExternalLink className="w-4 h-4 ml-2" />
          </Button>
        </div>
      )}

      {/* Second Sidebar Ad */}
      {sidebarAds.length > 1 && (
        <div>
          <AdBanner 
            ad={sidebarAds[1]} 
            onImpression={recordImpression}
            onClick={recordClick}
          />
        </div>
      )}
    </aside>
  );
};

// Inline Ad Component (for article content)
const InlineAd = ({ position }) => {
  const { getAdsByPosition, recordClick, recordImpression } = useAds();
  const ads = getAdsByPosition(position);
  
  if (ads.length === 0) return null;
  
  const ad = ads[0];
  
  return (
    <div className="my-8 p-4 bg-gray-50 rounded-xl">
      <AdBanner 
        ad={ad} 
        onImpression={recordImpression}
        onClick={recordClick}
      />
    </div>
  );
};

// Featured Products Banner (horizontal for article end)
const FeaturedProductsBanner = ({ relatedSpecies }) => {
  const { getProductsByAnimal, getFeaturedProducts } = useProducts();
  
  let products = [];
  if (relatedSpecies && relatedSpecies.length > 0) {
    relatedSpecies.forEach(species => {
      products = [...products, ...getProductsByAnimal(species)];
    });
    products = [...new Map(products.map(p => [p.id, p])).values()];
  }
  
  if (products.length === 0) {
    products = getFeaturedProducts();
  }
  
  products = products.slice(0, 4);
  
  if (products.length === 0) return null;

  return (
    <div className="bg-gradient-to-r from-green-50 to-emerald-50 py-8 mt-8 rounded-xl border border-green-100">
      <div className="px-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <ShoppingBag className="w-6 h-6 text-green-600" />
            <h3 className="text-xl font-bold text-gray-800">Notre sélection pour vous</h3>
          </div>
          <Button 
            variant="outline" 
            size="sm"
            className="text-green-600 border-green-300 hover:bg-green-100"
            onClick={() => window.location.href = '/boutique'}
          >
            Voir tout
          </Button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {products.map(product => (
            <a 
              key={product.id}
              href={product.affiliateUrl}
              target="_blank"
              rel="noopener noreferrer sponsored"
              className="bg-white rounded-xl p-3 border border-gray-100 hover:shadow-md transition-all group"
            >
              <img 
                src={product.image}
                alt={product.name}
                className="w-full h-24 object-cover rounded-lg mb-2 group-hover:scale-105 transition-transform"
              />
              <p className="text-xs text-gray-500">{product.brand}</p>
              <h4 className="font-medium text-sm text-gray-800 line-clamp-1 mb-1">{product.name}</h4>
              <div className="flex items-center gap-2">
                <span className="font-bold text-green-600 text-sm">{product.price}€</span>
                {product.originalPrice && (
                  <span className="text-xs text-gray-400 line-through">{product.originalPrice}€</span>
                )}
              </div>
            </a>
          ))}
        </div>
      </div>
    </div>
  );
};

const ConseilsPage = () => {
  const navigate = useNavigate();
  const { slug } = useParams();
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [relatedArticles, setRelatedArticles] = useState([]);

  // Fetch categories
  useEffect(() => {
    fetchCategories();
  }, []);

  // Fetch articles when filters change
  useEffect(() => {
    if (!slug) {
      fetchArticles();
    }
  }, [selectedCategory, searchQuery, slug]);

  // Fetch single article if slug is provided
  useEffect(() => {
    if (slug) {
      fetchArticleBySlug(slug);
    } else {
      setSelectedArticle(null);
    }
  }, [slug]);

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API_URL}/api/conseils/categories`);
      if (response.ok) {
        const data = await response.json();
        setCategories(data);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchArticles = async () => {
    setIsLoading(true);
    try {
      let url = `${API_URL}/api/conseils/?limit=20`;
      if (selectedCategory) url += `&category=${selectedCategory}`;
      if (searchQuery) url += `&search=${encodeURIComponent(searchQuery)}`;
      
      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        setArticles(data);
      }
    } catch (error) {
      console.error('Error fetching articles:', error);
    }
    setIsLoading(false);
  };

  const fetchArticleBySlug = async (articleSlug) => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/conseils/slug/${articleSlug}`);
      if (response.ok) {
        const data = await response.json();
        setSelectedArticle(data);
        
        // Fetch related articles
        const relatedRes = await fetch(`${API_URL}/api/conseils/related/${data.article_id}?limit=3`);
        if (relatedRes.ok) {
          setRelatedArticles(await relatedRes.json());
        }
        
        // Update meta tags for SEO
        document.title = `${data.meta_title || data.title} | vetoRDV`;
        const metaDesc = document.querySelector('meta[name="description"]');
        if (metaDesc) {
          metaDesc.setAttribute('content', data.meta_description || data.excerpt);
        }
      } else {
        navigate('/conseils');
      }
    } catch (error) {
      console.error('Error fetching article:', error);
      navigate('/conseils');
    }
    setIsLoading(false);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  // Single Article View with Ads and Products
  if (selectedArticle) {
    // Split content blocks to insert inline ad
    const contentBlocks = selectedArticle.content_blocks || [];
    const midPoint = Math.floor(contentBlocks.length / 2);
    
    return (
      <div className="min-h-screen flex flex-col bg-gray-50">
        <Header />
        <main className="flex-grow">
          {/* Article Header */}
          <div className="bg-white border-b">
            <div className="max-w-6xl mx-auto px-4 py-6">
              <button 
                onClick={() => navigate('/conseils')}
                className="flex items-center gap-2 text-gray-600 hover:text-green-600 mb-4"
                data-testid="back-to-conseils"
              >
                <ArrowLeft className="w-4 h-4" />
                Retour aux conseils
              </button>
              
              <div className="flex items-center gap-2 text-sm text-gray-500 mb-3">
                <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full flex items-center gap-1">
                  {CATEGORY_ICONS[selectedArticle.category]}
                  {categories.find(c => c.value === selectedArticle.category)?.label || selectedArticle.category}
                </span>
                <span>•</span>
                <Calendar className="w-4 h-4" />
                {formatDate(selectedArticle.published_at)}
                <span>•</span>
                <Eye className="w-4 h-4" />
                {selectedArticle.view_count} lectures
              </div>
              
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                {selectedArticle.title}
              </h1>
              
              <p className="text-xl text-gray-600">
                {selectedArticle.excerpt}
              </p>
            </div>
          </div>
          
          {/* Cover Image */}
          {selectedArticle.cover_image && (
            <div className="max-w-6xl mx-auto px-4 py-6">
              <img 
                src={selectedArticle.cover_image}
                alt={selectedArticle.title}
                className="w-full h-64 md:h-96 object-cover rounded-2xl"
              />
            </div>
          )}
          
          {/* Article Content with Sidebar */}
          <div className="max-w-6xl mx-auto px-4 py-8">
            <div className="grid lg:grid-cols-3 gap-8">
              {/* Main Content */}
              <article className="lg:col-span-2">
                <div className="prose prose-lg max-w-none">
                  {contentBlocks.slice(0, midPoint).map((block, index) => (
                    <ContentBlock key={index} block={block} />
                  ))}
                  
                  {/* Inline Ad in the middle */}
                  <InlineAd position="article-middle" />
                  
                  {contentBlocks.slice(midPoint).map((block, index) => (
                    <ContentBlock key={index + midPoint} block={block} />
                  ))}
                </div>
                
                {/* Tags */}
                {selectedArticle.tags?.length > 0 && (
                  <div className="mt-8 pt-6 border-t">
                    <div className="flex flex-wrap gap-2">
                      {selectedArticle.tags.map((tag, i) => (
                        <span key={i} className="px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-sm">
                          #{tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Share */}
                <div className="mt-8 pt-6 border-t flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Share2 className="w-5 h-5 text-gray-400" />
                    <span className="text-gray-600">Partager cet article</span>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => {
                      if (navigator.share) {
                        navigator.share({ title: selectedArticle.title, url: window.location.href });
                      }
                    }}>
                      Partager
                    </Button>
                  </div>
                </div>
                
                {/* Inline Ad at the end */}
                <InlineAd position="article-end" />
                
                {/* Featured Products Banner */}
                <FeaturedProductsBanner relatedSpecies={selectedArticle.related_species} />
              </article>
              
              {/* Sidebar */}
              <div className="lg:col-span-1">
                <div className="sticky top-24">
                  <Sidebar relatedSpecies={selectedArticle.related_species} />
                </div>
              </div>
            </div>
          </div>
          
          {/* Related Articles */}
          {relatedArticles.length > 0 && (
            <div className="bg-white py-12 mt-8">
              <div className="max-w-6xl mx-auto px-4">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Articles similaires</h2>
                <div className="grid md:grid-cols-3 gap-6">
                  {relatedArticles.map((article) => (
                    <ArticleCard key={article.article_id} article={article} onClick={() => navigate(`/conseils/${article.slug}`)} />
                  ))}
                </div>
              </div>
            </div>
          )}
          
          {/* CTA */}
          <div className="bg-gradient-to-r from-green-600 to-emerald-600 py-12">
            <div className="max-w-4xl mx-auto px-4 text-center text-white">
              <Heart className="w-12 h-12 mx-auto mb-4" />
              <h2 className="text-2xl font-bold mb-2">Prenez soin de votre animal</h2>
              <p className="mb-6 text-green-100">Trouvez un vétérinaire près de chez vous</p>
              <Button 
                onClick={() => navigate('/recherche')}
                className="bg-white text-green-600 hover:bg-green-50"
              >
                Trouver un vétérinaire
              </Button>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // Articles List View with Sidebar
  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header />
      <main className="flex-grow">
        {/* Hero */}
        <div className="bg-gradient-to-br from-green-600 to-emerald-600 text-white py-12 md:py-16">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex items-center gap-3 mb-4">
              <BookOpen className="w-8 h-8" />
              <h1 className="text-3xl md:text-4xl font-bold">Conseils santé</h1>
            </div>
            <p className="text-xl text-green-100 max-w-2xl mb-8">
              Découvrez nos articles pour prendre soin de vos animaux au quotidien
            </p>
            
            {/* Search */}
            <div className="max-w-xl">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  placeholder="Rechercher un article..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-12 py-6 rounded-xl bg-white/10 border-white/20 text-white placeholder-white/60 focus:bg-white focus:text-gray-900 focus:placeholder-gray-400"
                  data-testid="search-articles"
                />
              </div>
            </div>
          </div>
        </div>
        
        {/* Categories */}
        <div className="bg-white border-b sticky top-0 z-10">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex items-center gap-2 py-4 overflow-x-auto scrollbar-hide">
              <button
                onClick={() => setSelectedCategory(null)}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                  !selectedCategory 
                    ? 'bg-green-500 text-white' 
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
                data-testid="category-all"
              >
                Tous
              </button>
              {categories.map((cat) => (
                <button
                  key={cat.value}
                  onClick={() => setSelectedCategory(cat.value)}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors flex items-center gap-1 ${
                    selectedCategory === cat.value 
                      ? 'bg-green-500 text-white' 
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                  data-testid={`category-${cat.value}`}
                >
                  <span>{cat.icon}</span>
                  {cat.label}
                  {cat.article_count > 0 && (
                    <span className="ml-1 px-1.5 py-0.5 bg-white/20 rounded text-xs">
                      {cat.article_count}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>
        
        {/* Articles Grid with Sidebar */}
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="grid lg:grid-cols-4 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-3">
              {isLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="w-8 h-8 border-4 border-green-500 border-t-transparent rounded-full animate-spin"></div>
                </div>
              ) : articles.length === 0 ? (
                <div className="text-center py-12">
                  <BookOpen className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                  <h3 className="text-xl font-semibold text-gray-600 mb-2">Aucun article trouvé</h3>
                  <p className="text-gray-500 mb-4">
                    {searchQuery 
                      ? `Aucun résultat pour "${searchQuery}"`
                      : 'Les articles arrivent bientôt ! Revenez prochainement.'}
                  </p>
                  {(searchQuery || selectedCategory) && (
                    <Button 
                      variant="outline" 
                      className="mt-4"
                      onClick={() => { setSearchQuery(''); setSelectedCategory(null); }}
                    >
                      Voir tous les articles
                    </Button>
                  )}
                </div>
              ) : (
                <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-6">
                  {articles.map((article) => (
                    <ArticleCard 
                      key={article.article_id} 
                      article={article} 
                      onClick={() => navigate(`/conseils/${article.slug}`)}
                    />
                  ))}
                </div>
              )}
            </div>
            
            {/* Sidebar on list page */}
            <div className="lg:col-span-1">
              <div className="sticky top-24">
                <Sidebar />
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

// Content Block Renderer
const ContentBlock = ({ block }) => {
  if (block.type === 'text') {
    return (
      <div 
        className="mb-6 text-gray-700 leading-relaxed"
        dangerouslySetInnerHTML={{ __html: block.content.replace(/\n/g, '<br/>') }}
      />
    );
  }
  if (block.type === 'heading') {
    const HeadingTag = `h${block.level || 2}`;
    return (
      <HeadingTag className="font-bold text-gray-900 mt-8 mb-4">
        {block.content}
      </HeadingTag>
    );
  }
  if (block.type === 'image') {
    return (
      <figure className="my-8">
        <img 
          src={block.url}
          alt={block.alt || block.caption || ''}
          className="w-full rounded-xl"
        />
        {block.caption && (
          <figcaption className="text-center text-gray-500 mt-2 text-sm">
            {block.caption}
          </figcaption>
        )}
      </figure>
    );
  }
  if (block.type === 'quote') {
    return (
      <blockquote className="border-l-4 border-green-500 pl-4 py-2 my-6 italic text-gray-600">
        {block.content}
        {block.author && <cite className="block mt-2 text-sm">— {block.author}</cite>}
      </blockquote>
    );
  }
  if (block.type === 'list') {
    const ListTag = block.ordered ? 'ol' : 'ul';
    return (
      <ListTag className="my-4 pl-6">
        {block.items?.map((item, i) => (
          <li key={i} className="mb-2">{item}</li>
        ))}
      </ListTag>
    );
  }
  return null;
};

// Article Card Component
const ArticleCard = ({ article, onClick }) => (
  <div 
    onClick={onClick}
    className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden cursor-pointer hover:shadow-lg hover:border-green-200 transition-all group"
    data-testid={`article-card-${article.article_id}`}
  >
    {article.cover_image ? (
      <img 
        src={article.cover_image}
        alt={article.title}
        className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
      />
    ) : (
      <div className="w-full h-48 bg-gradient-to-br from-green-100 to-emerald-100 flex items-center justify-center">
        <span className="text-6xl">{CATEGORY_ICONS[article.category] || '📝'}</span>
      </div>
    )}
    
    <div className="p-5">
      <div className="flex items-center gap-2 text-xs text-gray-500 mb-3">
        <span className="px-2 py-0.5 bg-green-50 text-green-700 rounded-full flex items-center gap-1">
          {CATEGORY_ICONS[article.category]}
          {article.category}
        </span>
        {article.featured && (
          <span className="px-2 py-0.5 bg-yellow-50 text-yellow-700 rounded-full">
            ⭐ À la une
          </span>
        )}
      </div>
      
      <h3 className="font-semibold text-gray-800 mb-2 line-clamp-2 group-hover:text-green-600 transition-colors">
        {article.title}
      </h3>
      
      <p className="text-sm text-gray-500 line-clamp-2 mb-4">
        {article.excerpt}
      </p>
      
      <div className="flex items-center justify-between text-xs text-gray-400">
        <span className="flex items-center gap-1">
          <Clock className="w-3 h-3" />
          {article.published_at ? new Date(article.published_at).toLocaleDateString('fr-FR') : 'Brouillon'}
        </span>
        <span className="flex items-center gap-1">
          <Eye className="w-3 h-3" />
          {article.view_count || 0}
        </span>
      </div>
    </div>
  </div>
);

export default ConseilsPage;
